import { BoundingPolyRect } from './BoundingPoly';

export const TEXT_OVERLAY_SIZES = ['small', 'medium', 'large', 'xl'] as const;
/**
 * Named size presets. These are shortcuts that set the numeric `fontScale`
 * (see TextOverlay.fontScale) to known values; the stored size is always the
 * continuous scale, not the label.
 */
export type TextOverlaySize = (typeof TEXT_OVERLAY_SIZES)[number];

export const TEXT_OVERLAY_HORIZONTAL_ALIGNS = [
  'left',
  'center',
  'right',
] as const;
export type TextOverlayHorizontalAlign =
  (typeof TEXT_OVERLAY_HORIZONTAL_ALIGNS)[number];

// The three vertical bands offered by the 3x3 Position grid picker.
export const TEXT_OVERLAY_VERTICAL_ALIGNS = [
  'top',
  'center',
  'bottom',
] as const;
// All vertical positions a box can be pinned to. `lowerThird` is a non-grid
// extra (a caption spot 1/3 up from the bottom) offered via an on-frame
// quick-place ring rather than the grid.
export const TEXT_OVERLAY_VERTICAL_POSITIONS = [
  'top',
  'center',
  'lowerThird',
  'bottom',
] as const;
export type TextOverlayVerticalAlign =
  (typeof TEXT_OVERLAY_VERTICAL_POSITIONS)[number];

/**
 * Everything AUTHORED about an on-screen text overlay: its text, its styling,
 * and where the user put it. This is the single source of truth — the box the
 * renderers and the burn-in engine draw is DERIVED from these fields by
 * computeTextOverlayPoly, never read back from storage as an independent value.
 *
 * The rect stored on each series item is a cache of that derivation, written
 * only by the save adapter so the engine has a box per time range. Treating it
 * as authoritative is what produced VE-30798: a stale rect contradicted the
 * pins here, and nothing could say which was right.
 *
 * Position is one of two mutually exclusive modes, discriminated by presence:
 * when a screenPosition* pin is set the box is anchored to that frame edge or
 * centre; when no pin is set the box sits at `customRect`.
 */
export interface TextOverlay {
  readonly text: string;
  readonly color: string;
  /**
   * Font size as a fraction of the frame (reference) height, e.g. 0.026. This
   * is a continuous value: the size presets (small/medium/large) are shortcuts
   * that set it to known values, and dragging a resize handle sets a custom
   * value. Stored resolution-independently so preview and burn-in agree.
   */
  readonly fontScale: number;
  readonly horizontalAlign: TextOverlayHorizontalAlign;
  readonly bold: boolean;
  readonly backgroundEnabled: boolean;
  /** Background shading colour (hex) shown behind the text when enabled. */
  readonly backgroundColor: string;
  /** Background shading opacity, 0 (transparent) to 1 (opaque). */
  readonly backgroundOpacity: number;
  /**
   * "Position on Screen" pins. When set, the box is anchored to that edge/
   * centre of the frame (so it stays put when the font size changes) instead
   * of floating at a custom drawn/dragged position. Undefined = custom
   * position (from click-to-place or a manual drag).
   */
  readonly screenPositionHorizontal?: TextOverlayHorizontalAlign;
  readonly screenPositionVertical?: TextOverlayVerticalAlign;
  /**
   * "Inset from edge" for an edge-pinned box: a fraction of the smaller frame
   * dimension (one of TEXT_OVERLAY_INSET_PRESETS — small/medium/large/xl).
   * Undefined = None (flush to the edge). Only affects edge positions, not
   * centre.
   */
  readonly screenInset?: number;
  /**
   * The custom (click-placed or dragged) position, used only when NO
   * screenPosition* pin is set — a pin always wins, and dragging to a custom
   * spot clears the pins. Only the position is honoured: the size is re-derived
   * from the text and fontScale, so this rect's width/height are a starting
   * point, not a constraint.
   *
   * Absent on overlays saved before this field existed; the load adapter hoists
   * it from the first series item's rect, so legacy assets upgrade on open.
   */
  readonly customRect?: BoundingPolyRect;
}
